import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";


/* --- proxy helper (same host/port as posters) --- */
const PROXY_BASE = process.env.REACT_APP_IMG_PROXY || "https://api.fansfolder.com";
const toProxied = (url) =>
  url ? `${PROXY_BASE}/img?u=${encodeURIComponent(String(url).split("?")[0])}` : "";

const MovieModal = ({ titleSvg, description, imageUrl, tags = [], onClose, buyUrl }) => {
  const [imgError, setImgError] = useState(false);
  const [imgTried, setImgTried] = useState(false);
  const backdropRef = useRef(null);
  const modalRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    document.body.style.overflow = "hidden";
    modalRef.current?.querySelector("button")?.focus();
    const onKeyDown = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
      previouslyFocused.current && previouslyFocused.current.focus?.();
    };
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === backdropRef.current) onClose?.();
    },
    [onClose]
  );

  const modalImgSrc = toProxied(imageUrl);
  const modalTitleSrc = toProxied(titleSvg);

  return ReactDOM.createPortal(
    <Backdrop
      ref={backdropRef}
      onMouseDown={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Movie details"
    >
      <Modal ref={modalRef} onMouseDown={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">x</CloseButton>

        <ModalImage>
          {!imgError ? (
            <Poster
              src={modalImgSrc}
              alt=""
              loading="lazy"
              decoding="async"
              onLoad={() => setImgTried(true)}
              onError={() => {
                setImgTried(true);
                setImgError(true);
              }}
            />
          ) : (
            <FallbackFrame>
              <p>Couldn’t load the preview image.</p>
              <small>Open link above to verify it loads (403/404 means bucket/object isn’t public).</small>
            </FallbackFrame>
          )}

          <GradientOverlay />
          {titleSvg ? <TitleOverlay src={modalTitleSrc} alt="" aria-hidden="true" /> : null}
        </ModalImage>

        <ModalDetails>
          <TagsContainer>
            {tags.slice(0, 8).map((tag, idx) => (
              <Tag key={idx}>{tag}</Tag>
            ))}
          </TagsContainer>

          <Description>{description}</Description>

          <ButtonsRow>
  {buyUrl ? (
    <WatchLink
      href={buyUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        console.log("Link clicked! Opening:", buyUrl);
        window.open(buyUrl, '_blank', 'noopener,noreferrer');
      }}
    >
      Watch Now
    </WatchLink>
  ) : (
    <WatchButton disabled>Watch Now</WatchButton>
  )}
</ButtonsRow>


          {!imgTried && <TinyNote>Loading preview…</TinyNote>}
        </ModalDetails>
      </Modal>
    </Backdrop>,
    document.getElementById("modal-root") || document.body
  );
};

export default MovieModal;

/* styles */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 20px;
`;

const Modal = styled.div`
  width: 667px;
  max-width: 100%;
  height: 607px;
  max-height: 100%;
  background: #111;
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalImage = styled.div`
  position: relative;
  width: 100%;
  height: 375px;
  overflow: hidden;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background: #333;
  @media (max-width: 568px){ height: 281px; }
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 80% -155%, rgba(17,17,17,0) 60%, rgba(17,17,17,0.1) 64%, rgba(17,17,17,1) 84%);
  pointer-events: none;
`;

const TitleOverlay = styled.img`
  position: absolute;
  bottom: 20px;
  left: 20px;
  max-width: 60%;
  height: auto;
  pointer-events: none;
  @media (max-width: 568px){ max-width: 80%; bottom: 12px; left: 12px; }
`;

const ModalDetails = styled.div`
  flex: 1;
  padding: 16px 40px 40px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;  // Add this
  z-index: 2;  // Add this
`;

const TagsContainer = styled.div` display: flex; flex-wrap: wrap; gap: 8px; `;
const Tag = styled.div` background: #333; padding: 3px 6px; border-radius: 2px; font-size: 14px; color: #ccc; white-space: nowrap; `;
const Description = styled.p` font-size: 15px; line-height: 1.4; color: #ddd; margin: 0; `;
const ButtonsRow = styled.div` display: flex; gap: 12px; margin-top: auto; `;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background: none;
  border: none;
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #EFAC00;
  }
`;

const WatchButton = styled.button`
  background: #EFAC00;
  color: #000;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
`;

const WatchLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #EFAC00;
  color: #000;
  text-decoration: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;  // Add this
  pointer-events: auto;  // Add this
`;

const FallbackFrame = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 12px;
  color: #bbb;
`;

const TinyNote = styled.small` color: #aaa; `;
